import React, { useState, useEffect } from 'react';
import { TrendingUp, Award, Target, BarChart3 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ManagerPerformance = ({ manager }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgentPerformance();
  }, []);

  const fetchAgentPerformance = async () => {
    try {
      const [agentsRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/sales-agents`),
        axios.get(`${BACKEND_URL}/api/users`)
      ]);

      const agentsData = agentsRes.data;
      const users = usersRes.data;

      // Calculate performance for each agent
      const performance = agentsData.map(agent => {
        const assignedClients = users.filter(u => u.assignedAgent === agent.agentId);
        const revenue = assignedClients.reduce((sum, u) => sum + (u.lifetimeValue || 0), 0);
        return {
          ...agent,
          clientCount: assignedClients.length,
          revenue
        };
      });

      // Sort by revenue
      performance.sort((a, b) => b.revenue - a.revenue);
      setAgents(performance);
    } catch (error) {
      console.error('Error fetching performance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Agent Performance</h2>
        <p className="text-slate-600">Compare and analyze agent metrics</p>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Leaderboard</h3>
        </div>
        <div className="p-6">
          {agents.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No agents yet</p>
          ) : (
            <div className="space-y-4">
              {agents.map((agent, index) => (
                <div key={agent.agentId} className="flex items-center space-x-4 p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-slate-200 text-slate-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-800">{agent.name}</h4>
                    <p className="text-sm text-slate-500">{agent.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-800">₹{agent.revenue.toLocaleString()}</p>
                    <p className="text-sm text-slate-500">{agent.clientCount} clients</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    {index === 0 && <Award className="w-5 h-5 text-yellow-500" />}
                    {agent.isActive ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Active</span>
                    ) : (
                      <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Inactive</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Top Performer</h3>
          </div>
          {agents[0] ? (
            <div>
              <p className="text-2xl font-bold text-slate-800">{agents[0].name}</p>
              <p className="text-sm text-slate-500">₹{agents[0].revenue.toLocaleString()} revenue</p>
            </div>
          ) : (
            <p className="text-slate-500">No data yet</p>
          )}
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Avg Revenue</h3>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              ₹{agents.length > 0 ? Math.round(agents.reduce((sum, a) => sum + a.revenue, 0) / agents.length).toLocaleString() : 0}
            </p>
            <p className="text-sm text-slate-500">Per agent</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Total Clients</h3>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-800">
              {agents.reduce((sum, a) => sum + a.clientCount, 0)}
            </p>
            <p className="text-sm text-slate-500">Across all agents</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagerPerformance;